const REDACTED_VALUE = '<redacted>';
const CIRCULAR_VALUE = '[Circular]';

const AWS_SIGNED_QUERY_PARAMS = new Set([
  'awsaccesskeyid',
  'expires',
  'signature',
  'x-amz-algorithm',
  'x-amz-credential',
  'x-amz-date',
  'x-amz-expires',
  'x-amz-security-token',
  'x-amz-signature',
  'x-amz-signedheaders',
]);

const DIRECT_OBJECT_STORAGE_HOST_MARKERS = [
  'amazonaws.com',
  'amazonaws.com.cn',
  'blob.core.windows.net',
  'cloudflarestorage.com',
  'r2.dev',
  'storage.googleapis.com',
];

const SENSITIVE_LOG_KEY_MARKERS = [
  'authorization',
  'authtoken',
  'accesstoken',
  'refreshtoken',
  'sessiontoken',
  'securitytoken',
  'password',
  'secret',
  'credential',
  'signature',
  'awsaccesskeyid',
];

class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000;
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.listeners = [];
  }

  _formatMessage(level, message, data) {
    return {
      level,
      message: this._redactLogValue(message),
      data: this._redactLogValue(data),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: this._redactLogValue(window.location.href),
      sessionId: this._getSessionId(),
    };
  }

  _redactLogValue(value, key = '', seen = new WeakSet()) {
    if (this._isSensitiveLogKey(key)) {
      return REDACTED_VALUE;
    }
    if (typeof value === 'string') {
      return this._redactLogText(value);
    }
    if (typeof value === 'bigint') {
      return value.toString();
    }
    if (typeof value === 'symbol' || typeof value === 'function') {
      return this._redactLogText(String(value));
    }
    if (value instanceof Error) {
      return this._redactErrorValue(value, seen);
    }
    if (Array.isArray(value)) {
      if (seen.has(value)) {
        return CIRCULAR_VALUE;
      }
      seen.add(value);
      try {
        return value.map(item => this._redactLogValue(item, '', seen));
      } finally {
        seen.delete(value);
      }
    }
    if (value && typeof value === 'object') {
      if (seen.has(value)) {
        return CIRCULAR_VALUE;
      }
      seen.add(value);
      try {
        return Object.fromEntries(
          Object.entries(value).map(([entryKey, entryValue]) => [
            entryKey,
            this._redactLogValue(entryValue, entryKey, seen),
          ])
        );
      } finally {
        seen.delete(value);
      }
    }
    return value;
  }

  _redactErrorValue(error, seen) {
    if (seen.has(error)) {
      return CIRCULAR_VALUE;
    }
    seen.add(error);
    try {
      const normalizedError = {
        name: this._redactLogValue(error.name, 'name', seen),
        message: this._redactLogValue(error.message, 'message', seen),
      };
      if (error.stack) {
        normalizedError.stack = this._redactLogValue(error.stack, 'stack', seen);
      }
      if ('cause' in error) {
        normalizedError.cause = this._redactLogValue(error.cause, 'cause', seen);
      }
      for (const [entryKey, entryValue] of Object.entries(error)) {
        if (!Object.prototype.hasOwnProperty.call(normalizedError, entryKey)) {
          normalizedError[entryKey] = this._redactLogValue(entryValue, entryKey, seen);
        }
      }
      return normalizedError;
    } finally {
      seen.delete(error);
    }
  }

  _redactLogText(value) {
    if (!value) {
      return value;
    }

    return value
      .split(/(\s+)/)
      .map(part => (this._isSensitiveLogToken(part) ? REDACTED_VALUE : part))
      .join('');
  }

  _isSensitiveLogToken(value) {
    return Boolean(value && !/^\s+$/.test(value) && this._containsSensitiveUrl(value));
  }

  _containsSensitiveUrl(value, seen = new Set()) {
    if (!value || seen.has(value)) {
      return false;
    }
    seen.add(value);

    const trimmedValue = String(value)
      .trim()
      .replace(/^["'<>(),;]+|["'<>(),;]+$/g, '');
    if (trimmedValue !== value) {
      return this._containsSensitiveUrl(trimmedValue, seen);
    }

    try {
      const parsed = new URL(value, window.location.origin);
      if (
        /\/(?:reset-password\/[^/]+\/[^/]+|verify-email\/[^/]+)/i.test(parsed.pathname) ||
        this._isDirectObjectStorageUrl(parsed) ||
        this._hasAwsSignedQueryParams(parsed.searchParams)
      ) {
        return true;
      }

      for (const nestedValue of parsed.searchParams.values()) {
        if (this._containsSensitiveUrl(nestedValue, seen)) {
          return true;
        }
      }

      if (parsed.hash && this._containsSensitiveUrl(parsed.hash.slice(1), seen)) {
        return true;
      }
    } catch (_error) {
      // Fall through to bare query parsing and percent-decoding below.
    }

    if (value.includes('=')) {
      const params = new URLSearchParams(value);
      if (this._hasAwsSignedQueryParams(params)) {
        return true;
      }

      for (const nestedValue of params.values()) {
        if (this._containsSensitiveUrl(nestedValue, seen)) {
          return true;
        }
      }
    }

    const embeddedValues = value
      .split(/\s+/)
      .map(part => part.trim().replace(/^["'<>(),;]+|["'<>(),;]+$/g, ''))
      .filter(Boolean);
    for (const embeddedValue of embeddedValues) {
      if (embeddedValue !== value && this._containsSensitiveUrl(embeddedValue, seen)) {
        return true;
      }
    }

    try {
      const decodedValue = decodeURIComponent(value);
      return decodedValue !== value && this._containsSensitiveUrl(decodedValue, seen);
    } catch (_error) {
      return false;
    }
  }

  _isDirectObjectStorageUrl(url) {
    if (url.protocol === 's3:') {
      return true;
    }
    const hostname = url.hostname.toLowerCase();
    return DIRECT_OBJECT_STORAGE_HOST_MARKERS.some(marker => hostname.includes(marker));
  }

  _hasAwsSignedQueryParams(params) {
    return Array.from(params.keys()).some(key => AWS_SIGNED_QUERY_PARAMS.has(key.toLowerCase()));
  }

  _isSensitiveLogKey(key) {
    const normalizedKey = String(key)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    return SENSITIVE_LOG_KEY_MARKERS.some(marker => normalizedKey.includes(marker));
  }

  _searchableLogText(value) {
    if (value === undefined || value === null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      const serialized = JSON.stringify(value);
      return serialized === undefined ? String(value) : serialized;
    } catch (_error) {
      return String(value);
    }
  }

  _logContainsSearchTerm(log, searchLower) {
    return (
      this._searchableLogText(log.message).toLowerCase().includes(searchLower) ||
      this._searchableLogText(log.data).toLowerCase().includes(searchLower)
    );
  }

  _getSessionId() {
    if (!this.sessionId) {
      this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return this.sessionId;
  }

  _addLog(log) {
    this.logs.push(log);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    for (const listener of [...this.listeners]) {
      try {
        listener(log);
      } catch (error) {
        console.error('Logger subscriber failed:', this._redactLogValue(error)); // eslint-disable-line no-console
      }
    }
  }

  _sendToServer(log) {
    if (!this.isDevelopment) {
      const headers = {
        'Content-Type': 'application/json',
      };
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers.Authorization = `Token ${token}`;
      }

      fetch('/api/logs/', {
        method: 'POST',
        headers,
        body: JSON.stringify(log),
      }).catch(err => {
        console.error('Failed to send log to server:', this._redactLogValue(err)); // eslint-disable-line no-console
      });
    }
  }

  log(message, data) {
    const log = this._formatMessage('info', message, data);
    if (this.isDevelopment) {
      console.log(`[${log.timestamp}] ${this._searchableLogText(log.message)}`, log.data || ''); // eslint-disable-line no-console
    }
    this._addLog(log);
  }

  info(message, data) {
    const log = this._formatMessage('info', message, data);
    if (this.isDevelopment) {
      console.info(`[${log.timestamp}] ${this._searchableLogText(log.message)}`, log.data || ''); // eslint-disable-line no-console
    }
    this._addLog(log);
  }

  warn(message, data) {
    const log = this._formatMessage('warning', message, data);
    console.warn(`[${log.timestamp}] ${this._searchableLogText(log.message)}`, log.data || ''); // eslint-disable-line no-console
    this._addLog(log);
  }

  error(message, data) {
    const log = this._formatMessage('error', message, data);
    console.error(`[${log.timestamp}] ${this._searchableLogText(log.message)}`, log.data || ''); // eslint-disable-line no-console
    this._addLog(log);
    this._sendToServer(log);
  }

  debug(message, data) {
    if (this.isDevelopment) {
      const log = this._formatMessage('debug', message, data);
      console.debug(`[${log.timestamp}] ${this._searchableLogText(log.message)}`, log.data || ''); // eslint-disable-line no-console
      this._addLog(log);
    }
  }

  performance(operation, duration, data) {
    const log = this._formatMessage('performance', operation, {
      duration,
      ...data,
    });
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(
        `[PERFORMANCE] ${this._searchableLogText(log.message)}: ${duration}ms`,
        log.data || ''
      );
    }
    this._addLog(log);

    if (duration > 1000) {
      this._sendToServer(log);
    }
  }

  api(method, url, status, duration, data) {
    const log = this._formatMessage('api', `${method} ${url}`, {
      method,
      url,
      status,
      duration,
      ...data,
    });

    if (this.isDevelopment || status >= 400) {
      // eslint-disable-next-line no-console
      console.log(
        `[API] ${this._searchableLogText(log.message)} - ${status} (${duration}ms)`,
        log.data || ''
      );
    }

    this._addLog(log);

    if (status >= 500) {
      this._sendToServer(log);
    }
  }

  jbrowse(event, data) {
    const log = this._formatMessage('jbrowse', event, data);
    if (this.isDevelopment) {
      console.log(`[JBrowse2] ${this._searchableLogText(log.message)}`, log.data || ''); // eslint-disable-line no-console
    }
    this._addLog(log);

    if (event === 'error' || event === 'crash') {
      this._sendToServer(log);
    }
  }

  trackHub(event, data) {
    const log = this._formatMessage('trackhub', event, data);
    if (this.isDevelopment) {
      console.log(`[TrackHub] ${this._searchableLogText(log.message)}`, log.data || ''); // eslint-disable-line no-console
    }
    this._addLog(log);

    if (event === 'validation_failed' || event === 'error') {
      this._sendToServer(log);
    }
  }

  getLogs(filter = {}) {
    let filteredLogs = [...this.logs];

    if (filter.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filter.level);
    }

    if (filter.startTime) {
      filteredLogs = filteredLogs.filter(
        log => new Date(log.timestamp) >= new Date(filter.startTime)
      );
    }

    if (filter.endTime) {
      filteredLogs = filteredLogs.filter(
        log => new Date(log.timestamp) <= new Date(filter.endTime)
      );
    }

    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      filteredLogs = filteredLogs.filter(log => this._logContainsSearchTerm(log, searchLower));
    }

    return filteredLogs;
  }

  clearLogs() {
    this.logs = [];
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  exportLogs() {
    const data = JSON.stringify(this.logs, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oikobrowser-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

const logger = new Logger();

if (process.env.NODE_ENV === 'development') {
  window.logger = logger;
}

export default logger;
